import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { CommerceToolsApi } from '@grandvisionhq/commercetools';
import { MediaQuery, Sticky, StickyCallToAction, Price } from '@grandvisionhq/common';
import { Hr } from '@grandvisionhq/elements';
import { useProductDetailSlots, useVto, useCmsData, NonRxProductCTA, RxGlassesCTA, ProductDetailTitleView, ProductDetailDescriptionView, ProductDetailDisclaimer, ProductDetailPrice, ProductDiscountLabel, Discount, TryOnButton, VirtualMirrorModal, ProductDetailUsps } from '@grandvisionhq/fun-products';
import { useReviewsContext, Actions, ReviewSnippet } from '@grandvisionhq/reviews';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { getLocalizedValue } from '@grandvisionhq/utils';
import { getActiveVariant, getVariants } from '../../helpers.js';
import { RxGlassesConfigurator } from '../../Prescription/Glasses/Configurator/RxGlassesConfigurator.js';
import ProductDetailOpticsFeatures from '../../ProductDetailOpticsFeatures/ProductDetailOpticsFeatures.js';
import ProductReimbursableLogo from '../../ProductReimbursableLogo/ProductReimbursableLogo.js';
import { pushPdpEventToDataLayer, pushOfferingSelectionEvent, pushAddToCartEvent } from '../../utils/DataLayer/data-layer.js';
import { mapVirtualMirrorVariants } from '../../utils/map-virtual-mirror-variants.js';
import { useProductViewHistory } from '../../utils/use-product-view-history.js';
import { selectOffering } from '../utils/offerings.js';
import { useProductDetailContext } from '../Context/ProductDetailContext.js';
import { useBrandLink } from '../utils/use-brand-link.js';
import { getFramesDisplayPrice } from '../utils/get-frames-display-price.js';
import { ProductImageGalleryContainer } from '../../ProductImageGallery/ProductImageGalleryContainer.js';
import { ProductDetailSpecificationContainer } from '../../ProductDetailSpecification/ProductDetailSpecificationContainer.js';
import { ProductLabelTag } from '../Components/ProductLabelTag/ProductLabelTag.js';
import { VariantPickerContainer } from '../Components/VariantPicker/VariantPickerContainer.js';
import { OfferingsListContainer } from '../Components/OfferingsList/OfferingsListContainer.js';
import SSP from '../../SSP/SSP.js';

const Frames = ({ activeSku , addToCartRef , onCTAClick , onPriceChange , product , showStickyButton , variant , opticianForm , children , stickyTop , secondaryCallToAction , handleConversionEvent , bookAppointmentRoute , storesRoute , sspConfig ,  })=>{
    var ref2;
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { currency , getLabel , hasLabel , locale  } = useIntl();
    const reviews = useReviewsContext();
    const productViewHistory = useProductViewHistory();
    const { reimbursableLogos , isReimbursable , filteredOfferings  } = useProductDetailContext();
    const brandLink = useBrandLink(product);
    const variants = React.useMemo(()=>[
            product.masterVariant,
            ...product.variants
        ]
    , [
        product
    ]);
    const openConfiguratorButtonRef = React.useRef(null);
    const { Slots  } = useProductDetailSlots();
    const { isVtoActive , isVtoFeatureEnabled  } = useVto();
    const { isPrescriptionGlassesEnabled , isProductLabelEnabled , featureConfigs  } = useFeatureToggles();
    const { rxGlassesConfiguratorDocument , disclaimer , usps: productTypeUsps  } = useCmsData();
    const [isConfiguratorOpen, setIsConfiguratorOpen] = React.useState(false);
    const offerings = React.useMemo(()=>{
        if (!product.offerings) {
            return [];
        }
        // filter offerings based on the isReimbursable
        return product.offerings.filter((offering)=>isReimbursable ? offering.isInsuranceReimbursable : !offering.isInsuranceReimbursable
        );
    }, [
        product.offerings,
        isReimbursable
    ]);
    const [selectedOffering1, setSelectedOffering] = React.useState(()=>{
        return selectOffering(offerings, (filteredOfferings === null || filteredOfferings === void 0 ? void 0 : filteredOfferings[0]) || '');
    });
    const [ctaAction, setCtaAction] = React.useState(()=>getCtaAction(variant, selectedOffering1, isPrescriptionGlassesEnabled)
    );
    const activeVariant = getActiveVariant(product, activeSku);
    const { price: variantPrice  } = variant;
    /**
   * SSP price handling
   */ const [sspState, setSspState] = React.useState(()=>({
            selectedGlasses: 'frameAndMonofocal'
        })
    );
    const handleSspChange = React.useCallback((newState)=>{
        setSspState((state)=>swcHelpers.objectSpread({}, state, newState)
        );
    }, []);
    const displayPrice = getFramesDisplayPrice(variantPrice, selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.price, !isPrescriptionGlassesEnabled ? sspState.totalPrice : undefined);
    const isVirtualMirrorEnabled = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'VIRTUAL_MIRROR';
    // Add SKU to viewed product history
    React.useEffect(()=>{
        // We only want 1 SKU(variant) per product to be in the view history
        productViewHistory.remove(variants.map((v)=>v.sku || ''
        ));
        productViewHistory.add(variant.sku);
    // Adding productViewHistory to deps will cause an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        variants.length,
        variant.sku
    ]);
    // Make sure the ReviewsDisplay component gets updated with the currently active SKU
    React.useEffect(()=>{
        if (reviews.enabled) {
            reviews.dispatch(Actions.updateReviewDisplay({
                variantId: activeSku
            }));
        }
    }, [
        activeSku,
        reviews,
        reviews.enabled
    ]);
    React.useEffect(()=>{
        pushPdpEventToDataLayer(DataLayer.DataLayerTypes.PurchaseType.OneTime, product, activeSku, currency);
    }, [
        activeSku
    ]);
    React.useEffect(()=>{
        if (selectedOffering1) {
            pushOfferingSelectionEvent(selectedOffering1, {
                isDefaultSelection: true
            });
        }
    // this useEffect should be executed only once, so no dependency is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleOfferingSelection = React.useCallback((selectedOffering)=>{
        setSelectedOffering(selectedOffering);
        pushOfferingSelectionEvent(selectedOffering);
        // update URL with selected offering
        const url = new URL(window.location.href);
        url.searchParams.set('offerings', getLocalizedValue(selectedOffering.title, locale));
        window.history.replaceState(null, '', url.toString());
    }, // changes on any of the dependencies has no effect on this function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const handleCtaClick = React.useCallback(()=>{
        onCTAClick();
        // in case of add to cart, we need to push the event to data layer
        if (ctaAction === 'ADD_TO_CART' && selectedOffering1) {
            pushAddToCartEvent(product, activeSku, selectedOffering1, currency);
        }
    }, [
        ctaAction,
        onCTAClick,
        activeSku,
        selectedOffering1,
        product,
        currency
    ]);
    React.useEffect(()=>{
        const activeAction = getCtaAction(variant, selectedOffering1, isPrescriptionGlassesEnabled);
        setCtaAction(activeAction);
    }, [
        activeSku,
        activeVariant,
        isPrescriptionGlassesEnabled,
        selectedOffering1,
        variant
    ]);
    const handleConfiguratorClose = React.useCallback(()=>{
        var ref;
        setIsConfiguratorOpen(false);
        openConfiguratorButtonRef === null || openConfiguratorButtonRef === void 0 ? void 0 : (ref = openConfiguratorButtonRef.current) === null || ref === void 0 ? void 0 : ref.focus();
    }, [
        openConfiguratorButtonRef
    ]);
    const handleConfiguratorOpen = React.useCallback(()=>{
        handleConversionEvent();
        setIsConfiguratorOpen(true);
    }, []);
    const mappedVtoVariants = isVirtualMirrorEnabled ? mapVirtualMirrorVariants(variants, activeSku, locale) : undefined;
    const callToActionButton = CommerceToolsApi.isRegularProductAction(ctaAction) ? /*#__PURE__*/ React.createElement(NonRxProductCTA, {
        storesRoute: storesRoute,
        action: ctaAction,
        handleConversionEvent: handleConversionEvent,
        addToCartConfig: {
            activeSku,
            offeringSku: selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.sku,
            bundleLineItemSkus: selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.bundledProducts,
            onSuccess: handleCtaClick
        }
    }) : /*#__PURE__*/ React.createElement(RxGlassesCTA, {
        bookAppointmentRoute: bookAppointmentRoute,
        action: ctaAction,
        handleConversionEvent: handleConversionEvent,
        buttonRef: openConfiguratorButtonRef,
        onClickWhenOrderable: handleConfiguratorOpen
    });
    var ref1, _useIncludedFrame, _useIncludedLensPack;
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__main product-detail__main--optical"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "desktop-hidden product-detail__header--mobile"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink
    }), isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    })), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__image-gallery"
    }, /*#__PURE__*/ React.createElement(ProductImageGalleryContainer, null)), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__description"
    }, /*#__PURE__*/ React.createElement(ProductDetailDescriptionView, {
        header: variant.header,
        description: variant.description,
        usps: variant.usps
    }, /*#__PURE__*/ React.createElement(ProductDetailOpticsFeatures, {
        frame: getLabel('products.frame'),
        antiReflex: getLabel('products.lens.features.antiReflex'),
        antiScratch: getLabel('products.lens.features.antiScratch')
    }))), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification"
    }, /*#__PURE__*/ React.createElement(ProductDetailSpecificationContainer, null)), disclaimer && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__disclaimer"
    }, /*#__PURE__*/ React.createElement(ProductDetailDisclaimer, null)), /*#__PURE__*/ React.createElement("section", {
        className: "product-detail__aside",
        "aria-label": `${variant.brand} ${variant.modelName}`
    }, /*#__PURE__*/ React.createElement(Sticky, {
        top: stickyTop || 0,
        bottomBoundary: ".product-detail",
        enabled: !isSmallScreen
    }, /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: "mobile-hidden"
    }, /*#__PURE__*/ React.createElement(ProductDetailTitleView, {
        title: `${variant.brand} ${variant.modelName}`,
        subtitle: variant.brand,
        href: brandLink
    })), activeVariant.isInsuranceReimbursable && /*#__PURE__*/ React.createElement(ProductReimbursableLogo, {
        logos: reimbursableLogos,
        variant: isReimbursable ? 'alternative' : 'default'
    }), !isProductLabelEnabled && variant.tag && variant.tag.value && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__product-tag"
    }, /*#__PURE__*/ React.createElement(ProductLabelTag, null)), !!variantPrice && /*#__PURE__*/ React.createElement("div", {
        role: "group",
        "aria-label": getLabel('products.screenreader.priceInformation')
    }, !!(selectedOffering1 === null || selectedOffering1 === void 0 ? void 0 : selectedOffering1.bundleContentDescription) && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__offering-bundle-description"
    }, getLocalizedValue(selectedOffering1.bundleContentDescription, locale)), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification-prices"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__price-container"
    }, /*#__PURE__*/ React.createElement(ProductDetailPrice, swcHelpers.extends({}, displayPrice))), isProductLabelEnabled && /*#__PURE__*/ React.createElement(ProductDiscountLabel, {
        title: variant.discountText
    }), !isProductLabelEnabled && variant.discounted && /*#__PURE__*/ React.createElement(Discount, {
        label: variant.discountText,
        discount: variant.discounted,
        isWide: true
    })), Slots.AfterPrice ? /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__after-price-slot"
    }, /*#__PURE__*/ React.createElement(Slots.AfterPrice, null)) : null), !isSmallScreen && /*#__PURE__*/ React.createElement(ReviewSnippet, {
        className: "product-detail__review-snippet",
        productId: product.masterVariant.sku,
        variantId: activeSku
    }), hasLabel('products.frames.contents') && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__specification-contents"
    }, /*#__PURE__*/ React.createElement("span", {
        className: "product-detail__specification-contents__header"
    }, getLabel('products.including')), /*#__PURE__*/ React.createElement("p", null, getLabel('products.frames.contents'))), /*#__PURE__*/ React.createElement(VariantPickerContainer, {
        variantItems: getVariants(variants, getLocalizedValue(product.slug, locale), variant.productType, locale),
        activeVariantSku: variant.sku,
        activeVariantLabel: variant.frameColorLabel,
        enableStockIndicator: isPrescriptionGlassesEnabled,
        channelAvailability: variant.channelAvailability,
        stockCount: (ref1 = (ref2 = variant.availability) === null || ref2 === void 0 ? void 0 : ref2.availableQuantity) !== null && ref1 !== void 0 ? ref1 : 0,
        isAllowBackOrder: !!variant.allowBackOrder
    }), /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__cart-details",
        ref: addToCartRef
    }, offerings && selectedOffering1 && /*#__PURE__*/ React.createElement(OfferingsListContainer, {
        selectedValue: selectedOffering1.sku,
        offerings: offerings,
        onSelectionChange: handleOfferingSelection
    }), callToActionButton, secondaryCallToAction && secondaryCallToAction(variant), Slots.AfterCTA ? /*#__PURE__*/ React.createElement(Slots.AfterCTA, null) : null, activeVariant.vtoEnabled && isVtoFeatureEnabled && /*#__PURE__*/ React.createElement(TryOnButton, null), isVirtualMirrorEnabled && isVtoActive && /*#__PURE__*/ React.createElement(VirtualMirrorModal, {
        variants: mappedVtoVariants
    }), isSmallScreen && showStickyButton && !!variantPrice && /*#__PURE__*/ React.createElement(StickyCallToAction, {
        button: callToActionButton,
        price: /*#__PURE__*/ React.createElement(Price, {
            value: displayPrice.discountedPrice ? displayPrice.discountedPrice : displayPrice.originalPrice,
            currency: currency,
            centAmount: true,
            type: displayPrice.discountedPrice ? 'discount' : 'default'
        })
    })), productTypeUsps && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Hr, {
        className: "product-detail__usp-divider"
    }), /*#__PURE__*/ React.createElement(ProductDetailUsps, null))))), sspConfig && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__configuration"
    }, /*#__PURE__*/ React.createElement(SSP, {
        product: product,
        initialGlasses: sspConfig.initialGlassesKey,
        lensExtras: sspConfig.sspExtraProducts,
        lensExtrasResolver: sspConfig.sspResolver,
        onChange: handleSspChange,
        useIncludedFrame: (_useIncludedFrame = sspConfig.useIncludedFrame) !== null && _useIncludedFrame !== void 0 ? _useIncludedFrame : false,
        useIncludedLensPack: (_useIncludedLensPack = sspConfig.useIncludedLensPack) !== null && _useIncludedLensPack !== void 0 ? _useIncludedLensPack : true
    })), children && /*#__PURE__*/ React.createElement("div", {
        className: "product-detail__children"
    }, children), /*#__PURE__*/ React.createElement(RxGlassesConfigurator, {
        id: "glasses",
        frame: product,
        frameSku: activeSku,
        isOpen: isConfiguratorOpen,
        triggerButtonRef: openConfiguratorButtonRef,
        onModalClose: handleConfiguratorClose,
        onPriceChange: onPriceChange,
        opticianForm: opticianForm,
        configuratorDocument: rxGlassesConfiguratorDocument,
        usps: productTypeUsps
    }));
};
function getActiveOfferingActionObject(offering, activeSku) {
    if (!offering || !activeSku) {
        return;
    }
    if (Array.isArray(offering.actions)) {
        /**
     * 'offerings' will only be an array once we're using GraphQL to fetch the product data.
     */ return offering === null || offering === void 0 ? void 0 : offering.actions.find((a)=>a.variant === activeSku
        );
    } else {
        const action = offering.actions[activeSku];
        if (!action) {
            return;
        }
        return {
            variant: activeSku,
            action
        };
    }
}
function getCtaAction(variant, offering, isPrescriptionGlassesEnabled) {
    const fallbackAction = variant.channelAvailability === 'ONLINE' && isPrescriptionGlassesEnabled ? 'OPEN_CONFIGURATOR' : 'OPEN_CONFIGURATOR_UNAVAILABLE';
    if (!offering) {
        return fallbackAction;
    }
    if (offering && CommerceToolsApi.isDeprecatedOffering(offering)) {
        return offering.action;
    }
    const activeOffering = getActiveOfferingActionObject(offering, variant.sku);
    if (!activeOffering) {
        return fallbackAction;
    }
    return activeOffering.action;
}
var Frames$1 = Frames;

export { Frames, Frames$1 as default };
//# sourceMappingURL=Frames.js.map
